import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseButton, Combobox, Loader, Text, TextInput, useCombobox } from '@mantine/core';
import { DestinationSuggestion } from '@/interfaces/geocoding';
import { searchDestinations } from '@/services/geocoding.service';

interface SearchCityProps {
  stateValue: string;
  value: string;
  onChange: (value: string) => void;
  handleSubmit: (city: string, state: string) => void;
}

const getCityData = async (query: string, state: string): Promise<DestinationSuggestion[]> => {
  try {
    const suggestions = await searchDestinations(query);
    if (!state) {
      return suggestions;
    }
    return suggestions.filter((suggestion) =>
      suggestion.state.toLowerCase().includes(state.toLowerCase())
    );
  } catch {
    return [];
  }
};

const SearchCity = ({ stateValue, value, onChange, handleSubmit }: SearchCityProps) => {
  const { t } = useTranslation();
  const cityCombobox = useCombobox({
    onDropdownClose: () => cityCombobox.resetSelectedOption(),
  });
  const [cityData, setCityData] = useState<DestinationSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchOptions = (query: string) => {
    if (!query || query.length < 2) {
      setCityData([]);
      setEmpty(true);
      return;
    }

    setLoading(true);
    getCityData(query, stateValue)
      .then((result) => {
        setCityData(result);
        setLoading(false);
        setEmpty(result.length === 0);
        if (result.length > 0) {
          cityCombobox.openDropdown();
        }
      })
      .catch(() => {
        setLoading(false);
        setEmpty(true);
      });
  };

  const handleInputChange = (newValue: string) => {
    onChange(newValue);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (newValue.length >= 2) {
      timeoutRef.current = setTimeout(() => fetchOptions(newValue), 600);
    } else {
      setCityData([]);
      setEmpty(true);
    }
  };

  const handleOptionSubmit = (selected: DestinationSuggestion) => {
    onChange(selected.label);
    if (selected.kind === 'state') {
      handleSubmit('', selected.state);
    } else {
      handleSubmit(selected.name, selected.state);
    }
    cityCombobox.closeDropdown();
  };

  useEffect(() => {
    if (value && value.length >= 2) {
      fetchOptions(value);
    } else {
      setCityData([]);
      setEmpty(true);
    }
  }, [stateValue]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Combobox
      onOptionSubmit={(optionValue) => {
        const selected = cityData.find((suggestion) => suggestion.id === optionValue);
        if (selected) {
          handleOptionSubmit(selected);
        }
      }}
      withinPortal={false}
      store={cityCombobox}
    >
      <Combobox.Target>
        <TextInput
          w="100%"
          size="md"
          radius="md"
          styles={{
            input: {
              background: '#f5f0e6',
              borderColor: '#17313e',
              borderWidth: 1.5,
              fontWeight: 600,
              color: '#17313e',
            },
          }}
          placeholder={t('search-form-placeholder-destinations')}
          value={value}
          onChange={(event) => {
            const newValue = event.currentTarget.value;
            handleInputChange(newValue);
            cityCombobox.openDropdown();
            cityCombobox.resetSelectedOption();
          }}
          onClick={() => {
            cityCombobox.openDropdown();
          }}
          onFocus={() => {
            if (value && value.length >= 2 && !cityCombobox.dropdownOpened) {
              fetchOptions(value);
            }
          }}
          onBlur={() => {
            setTimeout(() => {
              cityCombobox.closeDropdown();
            }, 150);
          }}
          rightSection={
            loading ? (
              <Loader size={18} />
            ) : (
              value !== '' && (
                <CloseButton
                  size="xl"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange('');
                    setCityData([]);
                    setEmpty(true);
                  }}
                  aria-label={t('search-city-aria-label-clear')}
                />
              )
            )
          }
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {loading ? (
            <Combobox.Empty>{t('search-city-loading')}</Combobox.Empty>
          ) : empty ? (
            <Combobox.Empty>{t('search-city-no-cities-found')}</Combobox.Empty>
          ) : (
            cityData?.map((city) => (
              <Combobox.Option key={city.id} value={city.id}>
                <Text size="xl">{city.label}</Text>
              </Combobox.Option>
            ))
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default SearchCity;
